import { useInView } from '@/hooks/useInView';

const services = [
  {
    title: 'Daily Health Support',
    desc: 'Our carers visit from early morning through to late evening, covering medication prompts, health monitoring, and personal care throughout the day.',
    tag: 'Ongoing',
  },
  {
    title: 'Personal Care',
    desc: 'Respectful, dignified support with daily routines: bathing, dressing, grooming, mobility. Every interaction handled with patience and genuine warmth.',
    tag: 'Daily',
  },
  {
    title: 'Memory Care',
    desc: 'Specialist support for people living with dementia or Alzheimer\'s. Calm, consistent routines using proven techniques to reduce anxiety and build confidence.',
    tag: 'Specialist',
  },
  {
    title: 'Companionship',
    desc: 'Sometimes the most important thing is simply having someone there. Our carers build real relationships, not just professional ones.',
    tag: 'Flexible',
  },
  {
    title: 'Household Support',
    desc: 'Light housekeeping, meal preparation, shopping and errands. The everyday things that keep a home running and life feeling normal.',
    tag: 'Practical',
  },
  {
    title: 'Respite Care',
    desc: 'Short-term cover so family carers can rest, travel, or simply take a breath. Planned or at short notice, we\'re here when you need us.',
    tag: 'Short-term',
  },
];

function ServiceRow({ s, i }: { s: typeof services[0]; i: number }) {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className="group flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-8 py-8 border-b border-[#2C3E50]/8 last:border-0 transition-all duration-500 ease-out cursor-default"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : 'translateY(14px)',
        transitionDelay: `${i * 50}ms`,
      }}
    >
      <div className="shrink-0 w-20">
        <span className="text-[#C0706A]/50 text-[10px] font-mono tracking-widest">{String(i + 1).padStart(2, '0')}</span>
      </div>
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h3 className="text-[#2C3E50] font-bold text-lg group-hover:text-[#C0706A] transition-colors duration-200">
            {s.title}
          </h3>
          <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#C0706A] border border-[#C0706A]/30 px-2 py-0.5 rounded-full">
            {s.tag}
          </span>
        </div>
        <p className="text-[#2C3E50]/50 text-sm leading-relaxed max-w-xl">{s.desc}</p>
      </div>
      <div className="shrink-0 self-center">
        <button
          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          className="text-xs font-semibold text-[#2C3E50]/25 group-hover:text-[#C0706A] transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1"
        >
          Enquire
          <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform duration-200"></i>
        </button>
      </div>
    </div>
  );
}

export default function Services() {
  const { ref: headerRef, inView: headerInView } = useInView<HTMLDivElement>({ threshold: 0.2 });

  return (
    <section id="services" className="bg-[#FAFAF8] py-0">

      {/* Sticky-label layout */}
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:gap-20 py-20 md:py-28">

          {/* Left — sticky label */}
          <div className="lg:w-72 shrink-0 mb-12 lg:mb-0">
            <div
              ref={headerRef}
              className="lg:sticky lg:top-28 transition-all duration-700 ease-out"
              style={{ opacity: headerInView ? 1 : 0, transform: headerInView ? 'none' : 'translateY(16px)' }}
            >
              <p className="text-[#C0706A] text-[10px] tracking-[0.3em] uppercase font-semibold mb-6">
                What we offer
              </p>
              <h2
                className="text-[#2C3E50] font-bold leading-[1.15] mb-6"
                style={{ fontSize: 'clamp(1.8rem, 3vw, 2.4rem)' }}
              >
                Care that fits<br />
                around your life,<br />
                <span className="text-[#C0706A]">not ours.</span>
              </h2>
              <p className="text-[#2C3E50]/45 text-sm leading-relaxed mb-8">
                Every service is shaped around the individual: their needs, their routine, their home.
              </p>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 text-sm font-bold text-[#2C3E50] border-b border-[#2C3E50]/40 pb-0.5 hover:text-[#C0706A] hover:border-[#C0706A] transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Talk to us
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          {/* Right — scrolling service list */}
          <div className="flex-1 border-t border-[#2C3E50]/10">
            {services.map((s, i) => (
              <ServiceRow key={s.title} s={s} i={i} />
            ))}
          </div>

        </div>
      </div>

    </section>
  );
}
